import React, { useEffect, useState } from 'react';
import Table from '@awp/components/table';
import { FORM_MODE, Permissions } from '@awp/constants';
import Authorize from '@awp/components/Authorize';
import { Col, Form, FormLabel, Row } from 'react-bootstrap';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { Link, useSearchParams } from 'react-router-dom';
import { AwpContainer } from '@components/Common/AwpContainer';
import { useUrlParamTableFilterContext } from '@providers/UrlParamTableFilterContext';
import { HazardForecastAreasService } from '@components/HazardForecastArea/HazardForecastAreasService';
import { FormSelect } from '@components/Common/AwpFormInputs';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import { getAvalancheGeographicAreas } from '@stores/avalancheSlice';

const HazardForecastAreasTable = ({ data, loading, filteredData, setFilteredData }) => {
  const SEARCH_PARAM_PROGRAM = 'program';
  const SEARCH_PARAM_STATUS = 'status';
  const SEARCH_PARAM_INACTIVE_PROGRAMS = 'inactivePrograms';

  const getProgram = (programs) => {
    const param = searchParams.get(SEARCH_PARAM_PROGRAM);
    if (isValueEmpty(param)) return null;

    const programValue = Number(param);
    if (isNaN(programValue) || programValue <= 0) return null;

    return programs.find((p) => programValue === p?.value);
  };

  const getBooleanSearchParam = (paramName, defaultValue = true) => {
    switch (searchParams.get(paramName)) {
      case 'true':
        return true;
      case 'false':
        return false;
      default:
        return defaultValue;
    }
  };

  const { defaultPageSize, index, onChangeIndex, onChangePageSize, onChangeSearch, onChangeSort, search, sort } =
    useUrlParamTableFilterContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(getBooleanSearchParam(SEARCH_PARAM_STATUS));
  const [includeInactivePrograms, setIncludeInactivePrograms] = useState(
    getBooleanSearchParam(SEARCH_PARAM_INACTIVE_PROGRAMS, false),
  );

  const crudService = new HazardForecastAreasService(FORM_MODE.LIST);

  const avalanchePrograms = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'))
    .map((area) => {
      return {
        ...area,
        disabled: area.isDisabled, // isDisabled is used in FormSelect controls to disable items
        isDisabled: undefined, // We don't want select items to be disabled
      };
    })
    .filter((x) => (includeInactivePrograms ? true : !x.disabled));

  const [program, setProgram] = useState(getProgram(avalanchePrograms));

  const onChangeStatus = () => {
    const newValue = !statusFilter;
    setSearchParams((params) => {
      params.set(SEARCH_PARAM_STATUS, newValue);
      return params;
    });

    setStatusFilter(newValue);
  };

  const onChangeIncludeInactivePrograms = () => {
    const newValue = !includeInactivePrograms;
    setSearchParams((params) => {
      params.set(SEARCH_PARAM_INACTIVE_PROGRAMS, newValue);
      return params;
    });

    setIncludeInactivePrograms(newValue);
  };

  const onProgramChange = (myProgram) => {
    setSearchParams((params) => {
      if (myProgram?.value) {
        params.set(SEARCH_PARAM_PROGRAM, myProgram?.value);
      } else {
        params.delete(SEARCH_PARAM_PROGRAM);
      }

      return params;
    });

    setProgram(myProgram);
  };

  const hasSearch = (src) => {
    return src ? src.toLowerCase().includes(search.toLowerCase()) : false;
  };

  useEffect(() => {
    if (!data || data.length === 0) return;

    let filtered = program ? data.filter((x) => x.ParentId === program.value) : data;

    if (!isValueEmpty(search)) {
      filtered = filtered.filter((f) => hasSearch(f.Name));
    }

    filtered = filtered.filter((x) => (statusFilter ? x.IsActive : !x.IsActive));

    setFilteredData(filtered);
  }, [data, search, statusFilter, program]);

  const columns = React.useMemo(() => HazardForecastAreasService.getColumns(), []);

  return (
    <AwpContainer>
      <Authorize requires={[Permissions.ReadAdminTools]}>
        <>
          <div className="container mb-3">
            <div className="row">
              <div className="col-sm-8 pl-0">{crudService.crud.getHeader()}</div>
            </div>
            <div className="container mb-3">
              <Formik
                enableReinitialize
                initialValues={{
                  includeInactivePrograms,
                  program,
                  active: statusFilter,
                  validateOnMount: true,
                }}
              >
                <Form>
                  <Row>
                    <Col md={4}>
                      <FormLabel>
                        <b>Search</b>
                      </FormLabel>
                      <input
                        id="search"
                        name="search"
                        className="form-control"
                        placeholder="Search..."
                        type="text"
                        value={search}
                        onChange={onChangeSearch}
                      />
                    </Col>
                    <Col md={4}>
                      <FormSelect
                        className="avalanche-select"
                        name="program"
                        label="Program"
                        isClearable
                        postChange={onProgramChange}
                        options={avalanchePrograms}
                      />
                    </Col>
                    <Col md={2} className="mb-4 m-auto">
                      <Form.Check
                        type="switch"
                        id="includeInactivePrograms"
                        label="Include Inactive Programs"
                        checked={includeInactivePrograms}
                        onChange={onChangeIncludeInactivePrograms}
                      />
                    </Col>
                    <Col md={2} className="mb-4 m-auto">
                      <Form.Check
                        type="switch"
                        id="active"
                        label="Status"
                        checked={statusFilter}
                        onChange={onChangeStatus}
                      />
                    </Col>
                  </Row>
                </Form>
              </Formik>
            </div>
          </div>
          <Table
            columns={columns}
            data={filteredData}
            fetchData={() => {}}
            loading={loading}
            defaultSortBy="Name"
            index={index}
            setIndex={onChangeIndex}
            defaultPageSize={defaultPageSize}
            setDefaultPageSize={onChangePageSize}
            sort={sort}
            setSort={onChangeSort}
          />
          <div className="container mb-3" style={{ paddingTop: '1.25rem' }}>
            <div className="row">
              <div className="col-md-12 col-lg-12 col-sm-12 pr-0 pl-0">
                <Authorize requires={[Permissions.WriteAdminTools]}>
                  <Link
                    to={`${FORM_MODE.CREATE}`}
                    className="mr-2 mr-mini-0 float-left btn btn-primary"
                    variant="primary"
                  >
                    Create
                  </Link>
                </Authorize>
              </div>
            </div>
          </div>
        </>
      </Authorize>
    </AwpContainer>
  );
};

export default HazardForecastAreasTable;
